/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type CreateItpInput, type StepInput } from '@therapy/shared-validators';

import { type TemplateGoal } from '../../lib/api/templates.js';

export type FormGoal = CreateItpInput['goals'][number];

export const EMPTY_GOAL = { title: '', measurementMethod: '', baseline: '', target: '' };

export function emptyToUndefined(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

/** Split a delimited free-text field into a trimmed, de-blanked list. */
function splitList(value: unknown, separator: string): string[] {
  if (typeof value !== 'string') {
    return [];
  }
  return value
    .split(separator)
    .map((part) => part.trim())
    .filter((part) => part !== '');
}

/** "PECS, DTT" → ['PECS','DTT']; empty → undefined (so the key is omitted). */
export function tagsFromText(value: unknown): string[] | undefined {
  const tags = splitList(value, ',');
  return tags.length > 0 ? tags : undefined;
}

/**
 * Parse the sub-targets field into the 3-tier shape. Sub-targets are separated by
 * `;`; an optional `>` introduces that sub-target's comma-separated teaching steps:
 * `"Imitate sounds > model, prompt; Two-word combos"` →
 * `[{ title: 'Imitate sounds', items: [{title:'model'},{title:'prompt'}] }, { title: 'Two-word combos' }]`.
 * Empty → undefined (the key is omitted).
 */
export function stepsFromText(value: unknown): StepInput[] | undefined {
  const subs = splitList(value, ';').map((chunk): StepInput => {
    const sepIndex = chunk.indexOf('>');
    if (sepIndex === -1) {
      return { title: chunk.trim() };
    }
    const title = chunk.slice(0, sepIndex).trim();
    const items = splitList(chunk.slice(sepIndex + 1), ',').map((t) => ({ title: t }));
    return items.length > 0 ? { title, items } : { title };
  });
  return subs.length > 0 ? subs : undefined;
}

/**
 * Map a template's goals into the form's goal shape. `approachTags` / `steps`
 * become the delimited text the inputs expect (joined back from arrays); the
 * form's `setValueAs` transforms re-split them on submit.
 */
export function templateToFormGoals(goals: readonly TemplateGoal[]): FormGoal[] {
  return goals.map((g) => ({
    title: g.title,
    category: g.category ?? '',
    measurementMethod: g.measurementMethod ?? '',
    baseline: g.baseline ?? '',
    target: g.target ?? '',
    approachTags: g.approachTags.join(', '),
    steps: g.steps.join('; '),
  })) as unknown as FormGoal[];
}
